import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import api from '@/utils/api';
import { CreditCard, User, UserPlus, Check, ShoppingBag, Truck, Shield, Tag, X } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useTenant } from '@/contexts/TenantContext';

export default function CheckoutPage() {
  const [processing, setProcessing] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [checkoutMode, setCheckoutMode] = useState(null); // 'guest' or 'account'
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' or 'paypal'
  const [cart, setCart] = useState([]);
  const [creditToApply, setCreditToApply] = useState(''); // string (raw input)
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingMethod, setShippingMethod] = useState('standard');
  // Coupon state — populated by /api/cart/coupon/preview before checkout.
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount, ... }
  const [couponError, setCouponError] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [guestForm, setGuestForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  });
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  
  useEffect(() => {
    checkAuth();
    loadCart();
  }, []);

  // Fetch shipping options whenever the cart changes (after auth resolves).
  useEffect(() => {
    if (isLoading || cart.length === 0) {
      setShippingOptions([]);
      return;
    }
    loadShippingOptions();
  }, [cart, isLoading, user]);

  const loadShippingOptions = async () => {
    try {
      const response = user
        ? await api.get('/checkout/shipping-quote')
        : await api.post('/checkout/shipping-quote/guest', { cart_items: cart });
      setShippingOptions(response.data.options || []);
    } catch (error) {
      setShippingOptions([]);
    }
  };
  
  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setCheckoutMode('account');
    } catch (error) {
      setUser(null);
      // Check for guest cart in localStorage
      const guestCart = localStorage.getItem('guestCart');
      if (guestCart) {
        setCart(JSON.parse(guestCart));
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadCart = async () => {
    try {
      const response = await api.get('/cart');
      setCart(response.data);
    } catch (error) {
      // If not logged in, use localStorage cart
      const guestCart = localStorage.getItem('guestCart');
      if (guestCart) {
        setCart(JSON.parse(guestCart));
      }
    }
  };
  
  const total = cart.reduce((sum, item) => sum + (item.total_price || 0), 0);

  // Coupon discount: validated by the server, but we mirror the math here so
  // the UI shows the correct breakdown without a round-trip on every render.
  // If the cart total drops below the previously-applied discount (e.g. user
  // removes an item), cap at the new total so we never show a negative line.
  const couponDiscount = appliedCoupon
    ? Math.max(0, Math.min(Number(appliedCoupon.discount) || 0, total))
    : 0;
  const subtotalAfterCoupon = Math.max(0, total - couponDiscount);

  // Pro Shop Credit (logged-in customers only) — applies AFTER the coupon.
  const availableCredit = Number(user?.pro_shop_credit || 0);
  const parsedCredit = parseFloat(creditToApply);
  const validCreditApplied = (() => {
    if (!user || availableCredit <= 0) return 0;
    if (Number.isNaN(parsedCredit) || parsedCredit <= 0) return 0;
    return Math.min(parsedCredit, availableCredit, subtotalAfterCoupon);
  })();
  const creditExceedsBalance = !Number.isNaN(parsedCredit) && parsedCredit > availableCredit + 1e-9;

  // Shipping cost is added on top, after coupon/credit (never discounted).
  const selectedShippingOption = shippingOptions.find(o => o.method === shippingMethod) || shippingOptions[0];
  const shippingCost = selectedShippingOption ? selectedShippingOption.cost : 0;

  const displayTotal = Math.max(0, subtotalAfterCoupon - validCreditApplied) + shippingCost;

  const applyMaxCredit = () => {
    const max = Math.min(availableCredit, subtotalAfterCoupon);
    setCreditToApply(max > 0 ? max.toFixed(2) : '');
  };

  const handleApplyCoupon = async () => {
    const code = (couponInput || '').trim();
    if (!code) {
      setCouponError('Enter a coupon code');
      return;
    }
    setCouponError('');
    setCouponBusy(true);
    try {
      const endpoint = user ? '/cart/coupon/preview' : '/cart/coupon/preview-guest';
      const body = user ? { code } : { code, subtotal: total, cart_items: cart };
      const res = await api.post(endpoint, body);
      if (!res.data?.valid) {
        setAppliedCoupon(null);
        setCouponError(res.data?.error || 'Coupon code is invalid.');
        return;
      }
      setAppliedCoupon(res.data);
      setCouponInput(res.data.code);
      toast.success(`Coupon "${res.data.code}" applied`);
    } catch (err) {
      setAppliedCoupon(null);
      setCouponError(err.response?.data?.detail || 'Failed to validate coupon');
    } finally {
      setCouponBusy(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
  };
  
  const handleCheckout = async () => {
    if (paymentMethod === 'stripe') {
      await handleStripeCheckout();
    } else if (paymentMethod === 'paypal') {
      await handlePayPalCheckout();
    }
  };

  const handlePayPalCheckout = async () => {
    if (!user && checkoutMode === 'guest') {
      // Validate guest form
      if (!guestForm.email || !guestForm.firstName || !guestForm.lastName || 
          !guestForm.address || !guestForm.city || !guestForm.state || !guestForm.zipCode) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      if (cart.length === 0) {
        toast.error('Your cart is empty');
        return;
      }
      
      try {
        setProcessing(true);
        const response = await api.post('/checkout/paypal/guest', {
          payment_method: 'paypal',
          email: guestForm.email,
          first_name: guestForm.firstName,
          last_name: guestForm.lastName,
          shipping_address: shippingMethod === 'pickup' ? {} : {
            address: guestForm.address,
            city: guestForm.city,
            state: guestForm.state,
            zip_code: guestForm.zipCode,
            country: guestForm.country
          },
          cart_items: cart,
          coupon_code: appliedCoupon?.code || null,
          shipping_method: shippingMethod,
        });
        
        // Redirect to PayPal
        window.location.href = response.data.approval_url;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create PayPal checkout');
        setProcessing(false);
      }
    } else {
      // Logged in user checkout with PayPal
      try {
        setProcessing(true);
        const response = await api.post('/checkout/paypal', {
          payment_method: 'paypal',
          pro_shop_credit_to_apply: validCreditApplied,
          coupon_code: appliedCoupon?.code || null,
          shipping_method: shippingMethod,
        });
        
        // Redirect to PayPal
        window.location.href = response.data.approval_url;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create checkout session');
        setProcessing(false);
      }
    }
  };
  
  const handleStripeCheckout = async () => {
    if (!user && checkoutMode === 'guest') {
      // Validate guest form (address not required for in-store pickup)
      if (!guestForm.email || !guestForm.firstName || !guestForm.lastName ||
          (shippingMethod !== 'pickup' && (!guestForm.address || !guestForm.city || !guestForm.state || !guestForm.zipCode))) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      if (cart.length === 0) {
        toast.error('Your cart is empty');
        return;
      }
      
      try {
        setProcessing(true);
        const response = await api.post('/checkout/guest', {
          payment_method: 'stripe',
          email: guestForm.email,
          first_name: guestForm.firstName,
          last_name: guestForm.lastName,
          shipping_address: shippingMethod === 'pickup' ? {} : {
            address: guestForm.address,
            city: guestForm.city,
            state: guestForm.state,
            zip_code: guestForm.zipCode,
            country: guestForm.country
          },
          cart_items: cart,
          coupon_code: appliedCoupon?.code || null,
          shipping_method: shippingMethod,
        });
        
        // Clear guest cart on successful checkout initiation
        localStorage.removeItem('guestCart');
        
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create checkout session');
        setProcessing(false);
      }
    } else {
      // Logged in user checkout
      try {
        setProcessing(true);
        const response = await api.post('/checkout/stripe', {
          payment_method: 'stripe',
          pro_shop_credit_to_apply: validCreditApplied,
          coupon_code: appliedCoupon?.code || null,
          shipping_method: shippingMethod,
        });
        
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create checkout session');
        setProcessing(false);
      }
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Header />
        <div className="container mx-auto px-6 py-24 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#4A6741] border-r-transparent"></div>
          <p className="mt-4 text-[#5A6B5C]">Loading checkout...</p>
        </div>
      </div>
    );
  }
  
  // If user is logged in, skip the guest/account selection
  if (user) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Header />
        
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
          <div className="max-w-4xl mx-auto">
            <h1 
              className="text-4xl md:text-5xl font-bold text-[#2C3E2D] mb-8"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="checkout-title"
            >
              Checkout
            </h1>
            
            {/* Logged in user info */}
            <div className="bg-white p-6 rounded-lg border border-[#E8E4DC] mb-6">
              <div className="flex items-center space-x-3 text-[#4A6741]">
                <Check className="h-5 w-5" />
                <span className="font-medium">Signed in as {user.email}</span>
              </div>
            </div>
            
            {/* Order Summary */}
            <div className="bg-white p-6 rounded-lg border border-[#E8E4DC] mb-6">
              <h2 className="text-xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Order Summary
              </h2>
              <div className="space-y-3 mb-4">
                {cart.map((item, index) => (
                  <div key={item.cart_item_id || item.product_id || index} className="flex justify-between text-sm">
                    <span className="text-[#5A6B5C]">{item.product_name} x{item.quantity}</span>
                    <span className="text-[#2C3E2D] font-medium">${item.total_price?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#E8E4DC] pt-4 space-y-3">
                {/* Coupon code (always visible) */}
                <div className="bg-[#F5F3EF] border border-[#E8E4DC] rounded-lg p-3" data-testid="coupon-section">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[#2C3E2D] flex items-center gap-1">
                      <Tag className="h-4 w-4" /> Promo Code
                    </span>
                    {appliedCoupon && (
                      <span className="text-sm font-bold text-[#4A6741]" data-testid="coupon-applied-label">
                        {appliedCoupon.code}
                      </span>
                    )}
                  </div>
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#5A6B5C]">
                        {appliedCoupon.discount_type === 'percent'
                          ? `${appliedCoupon.amount}% off subtotal`
                          : `$${Number(appliedCoupon.amount).toFixed(2)} off subtotal`}
                      </span>
                      <button
                        type="button"
                        onClick={handleRemoveCoupon}
                        className="text-xs font-medium text-[#5A6B5C] hover:text-[#2C3E2D] flex items-center gap-1"
                        data-testid="coupon-remove"
                      >
                        <X className="h-3 w-3" /> Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={couponInput}
                        onChange={(e) => {
                          setCouponInput(e.target.value.toUpperCase());
                          if (couponError) setCouponError('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleApplyCoupon();
                          }
                        }}
                        placeholder="Enter code"
                        className="flex-1 h-8 px-2 text-sm font-mono uppercase bg-white border border-[#E8E4DC] rounded focus:outline-none focus:ring-1 focus:ring-[#4A6741]"
                        data-testid="coupon-input"
                        disabled={couponBusy}
                      />
                      <button
                        type="button"
                        onClick={handleApplyCoupon}
                        disabled={couponBusy || !couponInput.trim()}
                        className="h-8 px-3 text-xs font-bold text-white bg-[#4A6741] rounded hover:bg-[#3A5733] disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="coupon-apply"
                      >
                        {couponBusy ? '…' : 'Apply'}
                      </button>
                    </div>
                  )}
                  {couponError && (
                    <p className="text-xs text-red-600 mt-1" data-testid="coupon-error">
                      {couponError}
                    </p>
                  )}
                </div>

                {/* Pro Shop Credit (logged-in only, balance > 0) */}
                {user && availableCredit > 0 && (
                  <div className="bg-[#F5F3EF] border border-[#E8E4DC] rounded-lg p-3" data-testid="pro-shop-credit-section">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-[#2C3E2D]">Pro Shop Credit Available</span>
                      <span className="text-sm font-bold text-[#4A6741]" data-testid="pro-shop-credit-available">${availableCredit.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#5A6B5C]">Apply $</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max={Math.min(availableCredit, total)}
                        step="0.01"
                        value={creditToApply}
                        onChange={(e) => setCreditToApply(e.target.value)}
                        placeholder="0.00"
                        className="flex-1 h-8 px-2 text-sm bg-white border border-[#E8E4DC] rounded focus:outline-none focus:ring-1 focus:ring-[#4A6741]"
                        data-testid="pro-shop-credit-input"
                      />
                      <button
                        type="button"
                        onClick={applyMaxCredit}
                        className="h-8 px-2 text-xs font-medium text-[#4A6741] hover:underline"
                        data-testid="pro-shop-credit-max"
                      >
                        Max
                      </button>
                      {creditToApply && (
                        <button
                          type="button"
                          onClick={() => setCreditToApply('')}
                          className="h-8 px-2 text-xs text-[#5A6B5C] hover:underline"
                          data-testid="pro-shop-credit-clear"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {creditExceedsBalance && (
                      <p className="text-xs text-red-600 mt-1" data-testid="pro-shop-credit-error">
                        Amount exceeds your available credit.
                      </p>
                    )}
                  </div>
                )}

                {shippingOptions.length > 0 && (
                  <div className="bg-[#F5F3EF] border border-[#E8E4DC] rounded-lg p-3" data-testid="shipping-method-section">
                    <div className="flex items-center gap-1 text-sm font-medium text-[#2C3E2D] mb-2">
                      <Truck className="h-4 w-4" /> Shipping Method
                    </div>
                    <div className="space-y-2">
                      {shippingOptions.map(opt => (
                        <label
                          key={opt.method}
                          className={`flex items-center justify-between p-2 rounded border cursor-pointer transition-colors ${
                            shippingMethod === opt.method ? 'border-[#4A6741] bg-white' : 'border-[#E8E4DC]'
                          }`}
                          data-testid={`shipping-method-option-${opt.method}`}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="shipping-method"
                              checked={shippingMethod === opt.method}
                              onChange={() => setShippingMethod(opt.method)}
                              className="accent-[#4A6741]"
                              data-testid={`shipping-method-${opt.method}`}
                            />
                            <span className="text-sm text-[#2C3E2D]">{opt.label}</span>
                          </div>
                          <span className="text-sm font-bold text-[#2C3E2D]">
                            {opt.cost > 0 ? `$${opt.cost.toFixed(2)}` : 'Free'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {(couponDiscount > 0 || validCreditApplied > 0) && (
                  <div className="flex justify-between text-sm text-[#5A6B5C]" data-testid="checkout-subtotal-row">
                    <span>Subtotal</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                )}
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-sm text-[#4A6741] font-medium" data-testid="coupon-discount-row">
                    <span>Coupon ({appliedCoupon.code}) applied</span>
                    <span>-${couponDiscount.toFixed(2)}</span>
                  </div>
                )}
                {validCreditApplied > 0 && (
                  <div className="flex justify-between text-sm text-[#4A6741] font-medium">
                    <span>Pro Shop Credit applied</span>
                    <span>-${validCreditApplied.toFixed(2)}</span>
                  </div>
                )}
                {selectedShippingOption && (
                  <div className="flex justify-between text-sm text-[#5A6B5C]" data-testid="checkout-shipping-row">
                    <span>Shipping ({selectedShippingOption.label})</span>
                    <span>{shippingCost > 0 ? `$${shippingCost.toFixed(2)}` : 'Free'}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold text-[#2C3E2D]">
                  <span>Total</span>
                  <span data-testid="checkout-total">${displayTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            {/* Payment */}
            <div className="bg-white p-8 rounded-lg border border-[#E8E4DC] mb-6">
              <h2 className="text-xl font-bold text-[#2C3E2D] mb-6">Select Payment Method</h2>
              
              <div className="space-y-4">
                {/* Credit Card Option */}
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  disabled={processing}
                  className={`w-full p-6 rounded-lg transition-all flex items-center justify-between group disabled:opacity-50 ${
                    paymentMethod === 'stripe' 
                      ? 'bg-[#4A6741]/10 border-2 border-[#4A6741]' 
                      : 'bg-[#F5F3EF] border-2 border-[#E8E4DC] hover:border-[#4A6741]'
                  }`}
                  data-testid="stripe-option-button"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${
                      paymentMethod === 'stripe' ? 'bg-[#4A6741]/20' : 'bg-[#4A6741]/10 group-hover:bg-[#4A6741]/20'
                    }`}>
                      <CreditCard className="h-6 w-6 text-[#4A6741]" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-[#2C3E2D] font-bold">Credit / Debit Card</h3>
                      <p className="text-[#5A6B5C] text-sm">Pay securely with Stripe</p>
                    </div>
                  </div>
                  {paymentMethod === 'stripe' && (
                    <div className="w-6 h-6 bg-[#4A6741] rounded-full flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  )}
                </button>
              </div>
              
              {/* Checkout Button */}
              <button
                onClick={handleCheckout}
                disabled={processing || cart.length === 0}
                className="w-full mt-6 p-4 rounded-lg font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-[#4A6741] hover:bg-[#3A5733]"
                data-testid="checkout-submit-button"
              >
                {processing ? 'Processing...' : `Pay $${displayTotal.toFixed(2)}`}
              </button>
            </div>
            
            <p className="text-[#5A6B5C] text-sm text-center">
              Your payment information is secure and encrypted.
            </p>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }
  
  // Guest checkout flow
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Header />
      
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 
            className="text-4xl md:text-5xl font-bold text-[#2C3E2D] mb-8"
            style={{ fontFamily: 'Chivo, sans-serif' }}
            data-testid="checkout-title"
          >
            Checkout
          </h1>
          
          {/* Checkout Mode Selection */}
          {!checkoutMode && (
            <div className="space-y-6 mb-8">
              {/* Recommendation Banner */}
              <div className="bg-[#4A6741]/10 border border-[#4A6741]/20 rounded-lg p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-[#4A6741] rounded-full flex items-center justify-center flex-shrink-0">
                    <UserPlus className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[#2C3E2D] font-bold mb-2">Create an account for a better experience</h3>
                    <ul className="text-[#5A6B5C] text-sm space-y-1">
                      <li className="flex items-center space-x-2">
                        <Check className="h-4 w-4 text-[#4A6741]" />
                        <span>Track your orders in real-time</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Check className="h-4 w-4 text-[#4A6741]" />
                        <span>Save your shipping addresses</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Check className="h-4 w-4 text-[#4A6741]" />
                        <span>View order history anytime</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Check className="h-4 w-4 text-[#4A6741]" />
                        <span>Faster checkout on future orders</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => navigate(tenantPath('/login'), { state: { from: { pathname: tenantPath('/checkout') } } })}
                  className="p-6 bg-[#4A6741] text-white rounded-lg hover:bg-[#3A5733] transition-all flex items-center space-x-4"
                  data-testid="login-to-checkout-button"
                >
                  <User className="h-8 w-8" />
                  <div className="text-left">
                    <h3 className="font-bold text-lg">Sign In</h3>
                    <p className="text-white/80 text-sm">Already have an account?</p>
                  </div>
                </button>
                
                <button
                  onClick={() => setCheckoutMode('guest')}
                  className="p-6 bg-white border-2 border-[#E8E4DC] rounded-lg hover:border-[#4A6741] transition-all flex items-center space-x-4"
                  data-testid="guest-checkout-button"
                >
                  <ShoppingBag className="h-8 w-8 text-[#5A6B5C]" />
                  <div className="text-left">
                    <h3 className="font-bold text-lg text-[#2C3E2D]">Guest Checkout</h3>
                    <p className="text-[#5A6B5C] text-sm">Continue without an account</p>
                  </div>
                </button>
              </div>
            </div>
          )}
          
          {/* Guest Checkout Form */}
          {checkoutMode === 'guest' && (
            <div className="space-y-6">
              {/* Still show the create account reminder */}
              <div className="bg-[#D4A55A]/10 border border-[#D4A55A]/30 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <UserPlus className="h-5 w-5 text-[#D4A55A]" />
                  <span className="text-[#2C3E2D] text-sm">
                    Want to track your order? <Link to={tenantPath("/register")} className="text-[#4A6741] font-bold hover:underline">Create an account</Link>
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Form Section */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Contact Information */}
                  <div className="bg-white p-6 rounded-lg border border-[#E8E4DC]">
                    <h2 className="text-xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      Contact Information
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-[#2C3E2D]">Email Address *</Label>
                        <Input
                          type="email"
                          value={guestForm.email}
                          onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
                          className="bg-[#F5F3EF] border-[#E8E4DC]"
                          placeholder="you@example.com"
                          data-testid="guest-email-input"
                        />
                        <p className="text-xs text-[#5A6B5C] mt-1">We'll send your order confirmation here</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-[#2C3E2D]">First Name *</Label>
                          <Input
                            value={guestForm.firstName}
                            onChange={(e) => setGuestForm({ ...guestForm, firstName: e.target.value })}
                            className="bg-[#F5F3EF] border-[#E8E4DC]"
                            placeholder="John"
                            data-testid="guest-firstname-input"
                          />
                        </div>
                        <div>
                          <Label className="text-[#2C3E2D]">Last Name *</Label>
                          <Input
                            value={guestForm.lastName}
                            onChange={(e) => setGuestForm({ ...guestForm, lastName: e.target.value })}
                            className="bg-[#F5F3EF] border-[#E8E4DC]"
                            placeholder="Doe"
                            data-testid="guest-lastname-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Shipping Address */}
                  <div className="bg-white p-6 rounded-lg border border-[#E8E4DC]">
                    <h2 className="text-xl font-bold text-[#2C3E2D] mb-4 flex items-center space-x-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      <Truck className="h-5 w-5" />
                      <span>Shipping Address</span>
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-[#2C3E2D]">Street Address *</Label>
                        <Input
                          value={guestForm.address}
                          onChange={(e) => setGuestForm({ ...guestForm, address: e.target.value })}
                          className="bg-[#F5F3EF] border-[#E8E4DC]"
                          placeholder="123 Main St"
                          data-testid="guest-address-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-[#2C3E2D]">City *</Label>
                          <Input
                            value={guestForm.city}
                            onChange={(e) => setGuestForm({ ...guestForm, city: e.target.value })}
                            className="bg-[#F5F3EF] border-[#E8E4DC]"
                            placeholder="New York"
                            data-testid="guest-city-input"
                          />
                        </div>
                        <div>
                          <Label className="text-[#2C3E2D]">State *</Label>
                          <Input
                            value={guestForm.state}
                            onChange={(e) => setGuestForm({ ...guestForm, state: e.target.value })}
                            className="bg-[#F5F3EF] border-[#E8E4DC]"
                            placeholder="NY"
                            data-testid="guest-state-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-[#2C3E2D]">ZIP Code *</Label>
                          <Input
                            value={guestForm.zipCode}
                            onChange={(e) => setGuestForm({ ...guestForm, zipCode: e.target.value })}
                            className="bg-[#F5F3EF] border-[#E8E4DC]"
                            placeholder="10001"
                            data-testid="guest-zip-input"
                          />
                        </div>
                        <div>
                          <Label className="text-[#2C3E2D]">Country</Label>
                          <Input
                            value={guestForm.country}
                            disabled
                            className="bg-[#F5F3EF] border-[#E8E4DC] opacity-60"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment */}
                  <div className="bg-white p-6 rounded-lg border border-[#E8E4DC]">
                    <h2 className="text-xl font-bold text-[#2C3E2D] mb-4 flex items-center space-x-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      <Shield className="h-5 w-5" />
                      <span>Payment</span>
                    </h2>
                    
                    <div className="space-y-3 mb-4">
                      {/* Credit Card Option */}
                      <button
                        onClick={() => setPaymentMethod('stripe')}
                        disabled={processing}
                        className={`w-full p-4 rounded-lg transition-all flex items-center space-x-4 disabled:opacity-50 ${
                          paymentMethod === 'stripe' 
                            ? 'bg-[#4A6741]/10 border-2 border-[#4A6741]' 
                            : 'bg-[#F5F3EF] border-2 border-[#E8E4DC] hover:border-[#4A6741]'
                        }`}
                        data-testid="guest-stripe-option"
                      >
                        <CreditCard className="h-6 w-6 text-[#4A6741]" />
                        <div className="text-left flex-1">
                          <h3 className="text-[#2C3E2D] font-bold text-sm">Credit / Debit Card</h3>
                        </div>
                        {paymentMethod === 'stripe' && (
                          <div className="w-5 h-5 bg-[#4A6741] rounded-full flex items-center justify-center">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </button>
                    </div>
                    
                    <button
                      onClick={handleCheckout}
                      disabled={processing || cart.length === 0}
                      className="w-full p-4 rounded-lg font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-[#4A6741] hover:bg-[#3A5733]"
                      data-testid="guest-checkout-submit"
                    >
                      {processing ? 'Processing...' : `Pay $${displayTotal.toFixed(2)}`}
                    </button>
                    
                    <p className="text-[#5A6B5C] text-xs text-center mt-4">
                      Your payment information is secure and encrypted.
                    </p>
                  </div>
                </div>
                
                {/* Order Summary Sidebar */}
                <div>
                  <div className="bg-white p-6 rounded-lg border border-[#E8E4DC] sticky top-24">
                    <h2 className="text-xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      Order Summary
                    </h2>
                    
                    {cart.length === 0 ? (
                      <p className="text-[#5A6B5C] text-center py-4">Your cart is empty</p>
                    ) : (
                      <>
                        <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                          {cart.map((item, index) => (
                            <div key={`${item.product_id}-${index}`} className="flex justify-between text-sm py-2 border-b border-[#E8E4DC] last:border-0">
                              <div>
                                <p className="text-[#2C3E2D] font-medium">{item.product_name}</p>
                                <p className="text-[#5A6B5C] text-xs">Qty: {item.quantity}</p>
                              </div>
                              <span className="text-[#2C3E2D] font-medium">${item.total_price?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="border-t border-[#E8E4DC] pt-4 space-y-2">
                          {/* Coupon code (guest sidebar) */}
                          <div className="bg-[#F5F3EF] border border-[#E8E4DC] rounded p-2 mb-2" data-testid="coupon-section-guest">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-[#2C3E2D] flex items-center gap-1">
                                <Tag className="h-3 w-3" /> Promo Code
                              </span>
                              {appliedCoupon && (
                                <span className="text-xs font-bold text-[#4A6741]">{appliedCoupon.code}</span>
                              )}
                            </div>
                            {appliedCoupon ? (
                              <button
                                type="button"
                                onClick={handleRemoveCoupon}
                                className="text-xs text-[#5A6B5C] hover:text-[#2C3E2D] flex items-center gap-1"
                              >
                                <X className="h-3 w-3" /> Remove
                              </button>
                            ) : (
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={couponInput}
                                  onChange={(e) => {
                                    setCouponInput(e.target.value.toUpperCase());
                                    if (couponError) setCouponError('');
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleApplyCoupon();
                                    }
                                  }}
                                  placeholder="Code"
                                  className="flex-1 h-7 px-2 text-xs font-mono uppercase bg-white border border-[#E8E4DC] rounded focus:outline-none focus:ring-1 focus:ring-[#4A6741]"
                                  disabled={couponBusy}
                                />
                                <button
                                  type="button"
                                  onClick={handleApplyCoupon}
                                  disabled={couponBusy || !couponInput.trim()}
                                  className="h-7 px-2 text-xs font-bold text-white bg-[#4A6741] rounded hover:bg-[#3A5733] disabled:opacity-50"
                                >
                                  Apply
                                </button>
                              </div>
                            )}
                            {couponError && (
                              <p className="text-xs text-red-600 mt-1">{couponError}</p>
                            )}
                          </div>

                          <div className="flex justify-between text-sm text-[#5A6B5C]">
                            <span>Subtotal</span>
                            <span>${total.toFixed(2)}</span>
                          </div>
                          {couponDiscount > 0 && (
                            <div className="flex justify-between text-sm text-[#4A6741] font-medium">
                              <span>Coupon ({appliedCoupon.code})</span>
                              <span>-${couponDiscount.toFixed(2)}</span>
                            </div>
                          )}
                          {shippingOptions.length > 0 && (
                            <div className="bg-white border border-[#E8E4DC] rounded-lg p-2 mb-2" data-testid="shipping-method-section-guest">
                              <div className="flex items-center gap-1 text-xs font-medium text-[#2C3E2D] mb-2">
                                <Truck className="h-3 w-3" /> Shipping Method
                              </div>
                              <div className="space-y-1">
                                {shippingOptions.map(opt => (
                                  <label
                                    key={opt.method}
                                    className={`flex items-center justify-between p-1.5 rounded border cursor-pointer ${
                                      shippingMethod === opt.method ? 'border-[#4A6741] bg-[#F5F3EF]' : 'border-[#E8E4DC]'
                                    }`}
                                    data-testid={`shipping-method-option-guest-${opt.method}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="radio"
                                        name="shipping-method-guest"
                                        checked={shippingMethod === opt.method}
                                        onChange={() => setShippingMethod(opt.method)}
                                        className="accent-[#4A6741]"
                                        data-testid={`shipping-method-guest-${opt.method}`}
                                      />
                                      <span className="text-xs text-[#2C3E2D]">{opt.label}</span>
                                    </div>
                                    <span className="text-xs font-bold text-[#2C3E2D]">
                                      {opt.cost > 0 ? `$${opt.cost.toFixed(2)}` : 'Free'}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="flex justify-between text-sm text-[#5A6B5C]">
                            <span>Shipping{selectedShippingOption ? ` (${selectedShippingOption.label})` : ''}</span>
                            <span>{selectedShippingOption ? (shippingCost > 0 ? `$${shippingCost.toFixed(2)}` : 'Free') : 'Calculated at payment'}</span>
                          </div>
                          <div className="flex justify-between text-lg font-bold text-[#2C3E2D] pt-2 border-t border-[#E8E4DC]">
                            <span>Total</span>
                            <span>${displayTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </>
                    )}
                    
                    <button
                      onClick={() => setCheckoutMode(null)}
                      className="w-full mt-4 text-[#5A6B5C] hover:text-[#4A6741] text-sm transition-colors"
                    >
                      ← Back to checkout options
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
